// @flow
import React, {useState, useCallback, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  Modal,
  Paper,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import SearchBar from '../../../../../elements/SearchBar/SearchBar';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {getImageUrl} from '../../../../../utils/image_provider';
import {colors} from '../../../../../assets/styles/colors';

import type {Node} from 'react';
import type {Product, Category, Pagination} from '../../../../../entities';
import {useNearScreen} from '../../../../../utils/react';

type Props = {|
  maxItems?: number,
  title: string,
  open: boolean,
  handleClose: () => void,
  type?: 'PRODUCT_CATEGORY' | 'PRODUCT',
  fetchProducts: ({
    queryFilter?: {[key: string]: string | any},
  }) => void,
  fetchCategories: ({
    queryFilter?: {[key: string]: string | any},
  }) => void,
  items?: Product[] | Category[],
  isFetchingItems?: boolean,
  pagination?: Pagination,
  setListItems: (any[]) => void,
  listItems: any[],
  resetListFilter: () => void,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'grid',
    justifyItems: 'center',
    alignItems: 'center',
  },
  loadingSpinner: {
    alignSelf: 'center',
    marginTop: theme.spacing(3),
  },
  paper: {
    display: 'block',
    width: 600,
    minHeight: 536,
    position: 'relative',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3),
      width: '100%',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  addButton: {
    width: '100%',
    padding: theme.spacing(1.75, 1),
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      display: 'none',
      visibility: 'hidden',
    },
  },
  closeBox: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  content: {
    width: '100%',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',

    '&[data-disabled="true"]': {
      pointerEvents: 'not-allowed',
      opacity: 0.5,
    },
  },
  nameProduct: {
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey800,
    margin: 'auto 8px',
  },
  costProduct: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    display: 'flex',
    alignItems: 'center',
    color: colors.grey1000,
  },
  imageItem: {
    borderRadius: '8px',
  },
  divider: {
    border: '1px solid #E8E8ED',
    height: '1px',
    width: '100%',
  },
  wrapperItem: {
    margin: '16px auto',
  },
  scrollableWrapper: {
    overflowY: 'auto',
    maxHeight: '60vh',
    margin: '16px auto',
    padding: '16px',
  },
  option: {
    width: '100%',
  },
  actionSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 22,
    fontWeight: 600,
    color: '#313135',
  },
}));

/**
 * @deprecated
 * @param {*} props
 * @returns
 */
//TODO: We will deprecate this component as it is not fully reusable,
//      instead we must use the new ModalSelectItems in elements folder.
export default function ModalSelectItems(props: Props) {
  const {
    maxItems,
    title,
    open,
    handleClose,
    type = 'PRODUCT',
    fetchProducts,
    items,
    fetchCategories,
    isFetchingItems,
    pagination,
    setListItems,
    listItems,
    resetListFilter,
  } = props;

  const classes = useStyles();
  const [savedSearchString, setSavedSearchString] = useState('');
  const [localSelectedItems, setLocalSelectedItems] = useState<any[]>([]);

  const buildQueryParamsCallback = useCallback(
    (
      searchString?: string
    ): {
      [key: string]: any,
    } => {
      let params: {[key: string]: any} = {};
      if (type === 'PRODUCT') {
        params = {
          enabled: 1,
          size: 10,
          querySize: 'MINIMAL',
        };
      } else if (type === 'PRODUCT_CATEGORY') {
        params = {
          enabled: 1,
          size: 10,
        };
      }

      if (searchString) {
        params.name = searchString;
        setSavedSearchString(searchString);
      }

      return params;
    },
    [type]
  );

  useEffect(() => {
    if (open) {
      const queryFilter = buildQueryParamsCallback(savedSearchString);

      if (type === 'PRODUCT')
        fetchProducts({queryFilter: {...queryFilter, page: 0}, addPage: false});
      else if (type === 'PRODUCT_CATEGORY') {
        delete queryFilter.enabled;
        fetchCategories({
          queryFilter: {...queryFilter, page: 0},
          addPage: false,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    buildQueryParamsCallback,
    fetchCategories,
    fetchProducts,
    savedSearchString,
    type,
  ]);

  useEffect(() => {
    if (listItems) setLocalSelectedItems(listItems);
  }, [listItems]);

  useEffect(() => {
    if (!open) {
      setSavedSearchString('');
    }
    return () => resetListFilter();
  }, [open, resetListFilter]);

  const {fromRef: nearScreenRef, isNearScreen} = useNearScreen({
    distance: '200px',
    once: false,
  });

  const handleAddPage = useCallback(() => {
    if (
      !isFetchingItems &&
      localSelectedItems &&
      pagination &&
      pagination.total > pagination.size &&
      localSelectedItems.length < pagination.total
    ) {
      const queryFilter = buildQueryParamsCallback(savedSearchString);

      if (queryFilter) {
        if (type === 'PRODUCT') {
          fetchProducts({queryFilter, addPage: true});
        } else if (type === 'PRODUCT_CATEGORY') {
          const filters = {...queryFilter};
          delete filters.enabled;
          fetchCategories({queryFilter: filters, addPage: true});
        }
      }
    }
  }, [
    isFetchingItems,
    localSelectedItems,
    pagination,
    buildQueryParamsCallback,
    savedSearchString,
    type,
    fetchProducts,
    fetchCategories,
  ]);

  useEffect(() => {
    if (!pagination) return;

    const totalPage = Math.ceil(pagination.total / pagination.size);

    if (isNearScreen && totalPage > pagination.page) {
      handleAddPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNearScreen, pagination]);

  const getDisabledOption = (id: string) => {
    if (!isNaN(maxItems)) {
      if (localSelectedItems.find(p => p.id === id)) return false;
      return localSelectedItems.length >= Number(maxItems);
    } else return false;
  };

  const handleAddItem = (e, index: number) => {
    const posItem: number = localSelectedItems.findIndex(
      i => items && i.id === items[index].id
    );

    const newList = [...localSelectedItems];
    if (e.target.checked) {
      if (posItem === -1 && items)
        newList.push({...items[index], checked: true});
    } else {
      if (posItem > -1) {
        newList.splice(posItem, 1);
      }
    }

    setLocalSelectedItems(newList);
  };

  const arrayListSearched = (): Node => {
    return (
      <div className={classes.scrollableWrapper}>
        <hr className={classes.divider} />
        {// $FlowFixMe
        items.map((p: any, index: number) => (
          <div key={`${index}-${p.name}`} className={classes.wrapperItem}>
            <FormControlLabel
              classes={{label: classes.item}}
              className={classes.option}
              control={
                <Checkbox
                  color="primary"
                  disabled={getDisabledOption(p.id) || !p.enabled}
                  checked={!!localSelectedItems.find(l => l.id === p.id)}
                  onChange={e => handleAddItem(e, index)}
                />
              }
              label={
                type === 'PRODUCT' ? (
                  <div
                    className={classes.item}
                    data-disabled={getDisabledOption(p.id) || !p.enabled}
                    style={{justifyContent: 'space-between'}}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        className={classes.imageItem}
                        alt={p.name}
                        src={getImageUrl(p.mainImageId)}
                        width="56"
                        height="56"
                      />
                      <div className={classes.nameProduct}>{p.name}</div>
                    </div>

                    <div className={classes.costProduct}>{p.price}</div>
                  </div>
                ) : (
                  <div
                    className={classes.item}
                    data-disabled={getDisabledOption(p.id) || !p.enabled}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {p.portraitImageId && (
                        <img
                          className={classes.imageItem}
                          alt={p.name}
                          src={getImageUrl(p.portraitImageId)}
                          width="56"
                          height="56"
                        />
                      )}
                      <div className={classes.nameProduct}>{p.name}</div>
                    </div>
                  </div>
                )
              }
            />
            <hr className={classes.divider} />
          </div>
        ))}
        <div ref={nearScreenRef}></div>
        {isFetchingItems && (
          <div
            style={{
              textAlign: 'center',
            }}
          >
            <CircularProgress />
          </div>
        )}
      </div>
    );
  };

  const handleSaveList = () => {
    setListItems(localSelectedItems);
    setLocalSelectedItems([]);
    handleClose();
  };

  const fetchItemsCallback = useCallback(
    (searchString?: string) => {
      const queryFilter = buildQueryParamsCallback(searchString);
      if (type === 'PRODUCT') {
        if (queryFilter) {
          fetchProducts({queryFilter});
        }
      } else if (type === 'PRODUCT_CATEGORY') {
        if (queryFilter) {
          const filters = {...queryFilter};
          delete filters.enabled;
          fetchCategories({queryFilter: filters});
        }
      }
    },
    [buildQueryParamsCallback, fetchCategories, fetchProducts, type]
  );

  const typeItems = type === 'PRODUCT_CATEGORY' ? 'categorías' : 'productos';

  return (
    <Modal
      className={classes.root}
      open={open}
      onClose={() => {
        setLocalSelectedItems([]);
        handleClose();
      }}
    >
      <Paper className={classes.paper}>
        <Box className={classes.closeBox}>
          <IconButton aria-label="close" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <Box className={classes.content}>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            className={classes.title}
          >
            {title}
          </Typography>

          <SearchBar
            mobile={true}
            placeholder={`Buscar ${typeItems}`}
            color="light"
            externalHandleSearch={fetchItemsCallback}
          />
          {items ? (
            <>{arrayListSearched()}</>
          ) : isFetchingItems ? (
            <div style={{width: 'fit-content', margin: 'auto'}}>
              <CircularProgress className={classes.loadingSpinner} />
            </div>
          ) : (
            <div style={{margin: '36px auto'}}>No se encontraron productos</div>
          )}
        </Box>
        <Box className={classes.actionSection}>
          <div
            className={classes.costProduct}
          >{`${localSelectedItems.length} ${typeItems} seleccionados`}</div>
          <CustomButton
            color="primary"
            variant="contained"
            size="large"
            onClick={handleSaveList}
            disabled={localSelectedItems.length === 0}
          >
            Agregar
          </CustomButton>
        </Box>
      </Paper>
    </Modal>
  );
}
